import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Phone, MessageCircle, FileQuestion, Clock, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/hooks/useLanguage";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useCMSContent } from "@/hooks/useCMSContent";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import DOMPurify from 'dompurify';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Helper function to sanitize HTML content
const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html);
};

const Support = () => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();
  const { content, loading: cmsLoading } = useCMSContent('support-page', language);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-contact-form', {
        body: {
          type: 'support',
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        }
      });

      if (error) throw error;

      toast.success(t('support.success_message'));
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('support.error_message'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const pageContent = content?.content || {};

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent">
            {pageContent.hero_title || 'Centre de Support'}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {pageContent.hero_description || 'Nous sommes là pour vous aider. Contactez-nous ou consultez notre FAQ.'}
          </p>
        </div>

        {/* Contact Methods */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">{pageContent.contact_title || 'Contactez-nous'}</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-card rounded-lg p-6 shadow-sm border text-center">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-gradient-primary flex items-center justify-center">
                  <Mail className="h-8 w-8 text-primary-foreground" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">{pageContent.email_title || 'Email'}</h3>
              <div className="text-muted-foreground mb-4" dangerouslySetInnerHTML={{ __html: sanitizeHTML(pageContent.email_description || 'Envoyez-nous un email') }} />
              <a href={`mailto:${settings.contact_email}`} className="text-primary hover:underline">
                {settings.contact_email}
              </a>
            </div>

            <div className="bg-card rounded-lg p-6 shadow-sm border text-center">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-gradient-primary flex items-center justify-center">
                  <Phone className="h-8 w-8 text-primary-foreground" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">{pageContent.phone_title || 'Téléphone'}</h3>
              <div className="text-muted-foreground mb-4" dangerouslySetInnerHTML={{ __html: sanitizeHTML(pageContent.phone_description || 'Appelez-nous') }} />
              <a href={`tel:${settings.support_phone}`} className="text-primary hover:underline">
                {settings.support_phone}
              </a>
            </div>

            <div className="bg-card rounded-lg p-6 shadow-sm border text-center">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-gradient-primary flex items-center justify-center">
                  <MessageCircle className="h-8 w-8 text-primary-foreground" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">{pageContent.chat_title || 'support.chat'}</h3>
              <div className="text-muted-foreground mb-4" dangerouslySetInnerHTML={{ __html: sanitizeHTML(pageContent.chat_description || 'Discutez avec nous') }} />
              <Button variant="outline">{pageContent.chat_button || 'Ouvrir le chat'}</Button>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="mb-16 max-w-2xl mx-auto">
          <div className="bg-card rounded-lg p-8 shadow-sm border">
            <h2 className="text-2xl font-bold mb-6">{pageContent.form_title || 'Envoyez-nous un message'}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('support.name_label')}</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('support.name_placeholder')}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('support.email_label')}</label>
                  <Input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t('support.email_placeholder')}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">{t('support.subject_label')}</label>
                <Input
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder={t('support.subject_placeholder')}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">{t('support.message_label')}</label>
                <Textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t('support.message_placeholder')}
                  rows={6}
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? t('support.sending') : t('support.send')}
              </Button>
            </form>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">{pageContent.faq_title || 'Questions Fréquentes'}</h2>
          
          <div className="max-w-3xl mx-auto">
            {pageContent.faq && Array.isArray(pageContent.faq) && pageContent.faq.length > 0 ? (
              <Accordion type="single" collapsible className="space-y-4">
                {pageContent.faq.map((item: any, index: number) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg px-6 border">
                    <AccordionTrigger className="text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(item.answer) }} />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                {t('support.no_faq', 'Aucune question disponible pour le moment.')}
              </p>
            )}
          </div>
        </section>

        {/* Business Hours */}
        <section className="bg-gradient-primary text-primary-foreground rounded-lg p-12 text-center">
          <Clock className="h-16 w-16 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">{pageContent.hours_title || 'Horaires de Support'}</h2>
          <div className="max-w-md mx-auto space-y-2 text-lg opacity-90" dangerouslySetInnerHTML={{ __html: sanitizeHTML(pageContent.hours_content || '<p>Lundi - Vendredi : 9h00 - 18h00</p><p>Samedi : 10h00 - 16h00</p><p>Dimanche : Fermé</p>') }} />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Support;
